import React from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import { useHistory } from '@useHistory/useHistoryCompat';
import { Typography, Icon, Tooltip } from '@hevoio/hevo-ui-toolkit';
import styles from './AppbarItem.module.scss';
import useKeyboardShortcutListener from '../../../../a11y/shortcuts/hooks/useKeyboardShortcutListener';

interface AppbarItemProps {
  navActive?: boolean;
  label?: string;
  iconName?: string;
  shortcut?: any;
  className?: string;
  drawerLink?: string;
  isExternalLink?: boolean;
  styles?: any;
  activeDrawerLink?: string;
  onClick?: Function;
  to?: string;
  expanded?: boolean;
  [key: string]: any;
}

export function AppbarItem({
  navActive,
  label,
  iconName,
  shortcut,
  className,
  drawerLink,
  activeDrawerLink,
  onClick,
  isExternalLink = false,
  to,
  expanded = false,
  ...props
}: AppbarItemProps) {
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  let drawerPath;
  if (drawerLink) {
    params.set('drawer', drawerLink);
    drawerPath = `${location.pathname}?${params.toString()}`;
  }

  const handleDrawerOpen = e => {
    onClick(drawerLink || to);

    if (drawerLink) {
      history.push({ search: params.toString() });
      e.preventDefault();
      return;
    }

    if (!isExternalLink) {
      history.push(`${to}`);
      e.preventDefault();
    }
  };

  useKeyboardShortcutListener(handleDrawerOpen, shortcut?.command, {
    priority: 0,
    terminal: 'match'
  });

  const drawerIsActive = drawerLink && drawerLink === activeDrawerLink;

  return (
    <a
      {...props}
      href={to || drawerPath}
      className={clsx(
        navActive && styles.active,
        styles.appbarItem,
        expanded && styles.expanded,
        drawerIsActive && styles.open,
        className
      )}
      onClick={handleDrawerOpen}
    >
      <Tooltip dataId={`appbar-item-${label}`} title={label} placement='right' disabled={expanded}>
        <div className={styles.itemContent}>
          <div className={styles.iconBox}>
            <Icon
              className={clsx(
                styles.appbarLinkIcon,
                navActive ? 'text-light' : 'text-icon-sub-600'
              )}
              name={iconName}
            />
          </div>

          <div className={clsx(styles.labelWrap, expanded && styles.labelWrapVisible)}>
            <Typography
              level='label-small'
              color={navActive ? 'light' : 'sub-600'}
              hoverColor='light'
              focusColor='light'
            >
              {label}
            </Typography>
          </div>
        </div>
      </Tooltip>
    </a>
  );
}
